<template>
    <div
        class="bo-mobile-lucene-search"
        v-click-outside="vcoConfig"
    >
        <div class="search-header">
            <button
                type="button"
                class="search-back"
                @click="closeSearchPanel"
            >
                <i class="icon-chevronleft"></i>
            </button>
            <div class="searchbar rounded">
                <div
                    class="search-selection"
                    :class="{ disabled: isHomePage }"
                    id="toggleButton"
                    @click="toggleCategoryFilter"
                >
                    <span>
                        {{ buttonText }}
                    </span>
                    <i class="icon-signalcellular4bar"></i>
                </div>
                <a
                    class="btn-search"
                    id="consumer_search_product_link"
                >
                    <i
                        class="icon-search"
                        @click="searchIfNeeded"
                    ></i>
                </a>
                <input
                    ref="searchInput"
                    type="search"
                    enterkeyhint="search"
                    id="dropdownSearchBar"
                    class="search-input"
                    :placeholder="generalTranslations?.search ?? 'Search'"
                    v-model="searchValue"
                    @keypress="checkKeyPress"
                />
                <div
                    v-if="searchValue"
                    class="search-clear"
                >
                    <i
                        class="icon-close"
                        @click="searchValue = ''"
                    ></i>
                </div>
                <div
                    v-if="showLoadingIcon"
                    class="loading-indicator"
                >
                    <div class="spinner"></div>
                </div>
            </div>
        </div>
        <div class="search-filter">
            <button
                v-for="field in searchFields"
                :key="field"
                class="btn-rounded filterBtn included"
                :class="isSearchFieldSelected(field) ? 'filter-select' : 'filter-unselect'"
                @click="filterControl(field)"
            >
                {{ searchFieldLabel(field) }}
            </button>
        </div>
        <bo-search-suggestions
            v-if="searchValue.trim() && !leavingForResults"
            class="search-items"
            :query="searchValue.trim()"
            :words="searchWords"
            :show-products="productFields.length > 0"
            :show-categories="searchCategories"
            :show-description="isSearchFieldSelected('description')"
            :products="productResults"
            :product-total="productTotal"
            :products-loading="productsLoading"
            :categories="categoryResults"
            :category-total="categoryTotal"
            :categories-loading="categoriesLoading"
            :results-current="resultsQuery === searchValue.trim()"
            :pin-view-all="false"
            :header-translations="headerTranslations"
            @select-product="clickProduct"
            @select-category="clickCategory"
            @view-all="goToSearchResults"
            @results-scroll="dismissKeyboard"
        ></bo-search-suggestions>
        <bo-search-history
            v-else
            :terms="searchHistory"
            :editing="editingSearchHistory"
            :max-rows="4"
            :translations="headerTranslations"
            @select="searchHistoryTerm"
            @remove="removeSearchHistoryTerm"
            @clear-all="clearSearchHistory"
            @edit="editingSearchHistory = $event"
        ></bo-search-history>
    </div>
</template>
<script>
import Vue from "vue";

Vue.component("bo-toast", require("@/v2/components/bo-toast.vue").default);
Vue.component("bo-search-suggestions", require("js/frontend/components/bo-search-suggestions.vue").default);
Vue.component("bo-search-history", require("js/frontend/components/bo-search-history.vue").default);

import productMixin from "@/v2/mixins/products.js";
import { searchPreferenceMixin } from "js/frontend/mixins/search-preference.js";

export default {
    mixins: [productMixin, searchPreferenceMixin],

    props: {
        productsUrl: {
            type: String,
            default: null,
        },
        searchResultUrl: {
            type: String,
            default: null,
        },
        homepageUrl: {
            type: String,
            default: null,
        },
        outletId: {
            type: String,
            default: null,
        },
        categoryId: {
            type: String,
            default: null,
        },
        generalTranslations: {
            type: Object,
            default: null,
        },
        headerTranslations: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            searchValue: "",
            productResults: [],
            productTotal: 0,
            productsLoading: false,
            categoryResults: [],
            categoryTotal: 0,
            categoriesLoading: false,
            // the query the shown results belong to
            resultsQuery: "",
            searchCategory: false,
            showSearch: false,
            vcoConfig: {
                handler: this.deactivateIfNeeded,
                middleware: this.ignoreLoadingElement,
            },
            showResult: false,
            isHomePage: true,
        };
    },

    watch: {
        searchValue(newValue, oldValue) {
            if (this.leavingForResults) return;

            if (newValue.trim() != "") {
                if (newValue !== oldValue) {
                    setTimeout(() => {
                        if (this.searchValue === newValue) {
                            this.searchIfNeeded();
                        }
                    }, 1000);
                }
            }
        },
    },

    computed: {
        buttonText() {
            return this.searchCategory
                ? this.headerTranslations?.category ?? "Category"
                : this.headerTranslations?.global ?? "Global";
        },

        showLoadingIcon() {
            return this.productsLoading || this.categoriesLoading || this.leavingForResults;
        },
    },

    methods: {
        filterControl(field) {
            if (this.toggleSearchField(field)) {
                this.searchIfNeeded();
            }
        },

        searchIfNeeded() {
            if (this.searchValue.trim() != "") {
                this.searchProductWithKeys();
            }
        },

        // products and categories load in parallel; each section renders as soon as its own response arrives
        searchProductWithKeys() {
            const search = this.searchValue.trim();
            const isCurrentSearch = () => search === this.searchValue.trim();
            this.resultsQuery = search;

            this.productsLoading = this.productFields.length > 0;
            this.categoriesLoading = this.searchCategories;

            if (this.productsLoading) {
                let payload = { params: {} };
                payload["params"]["luceneSearchString"] = search;
                payload["params"]["luceneSearchField"] = this.productFields;
                payload["params"]["limit"] = 6;
                payload["params"]["categoryId"] = this.categoryId;
                payload["params"]["isCategorySearch"] = this.searchCategory;

                const finish = (products = [], meta = null) => {
                    if (!isCurrentSearch()) return;
                    this.productResults = this.convertProductToSearchResult(products);
                    this.productTotal = meta?.total ?? this.productResults.length;
                    this.productsLoading = false;
                };
                this.getProducts(this.productsUrl, payload, finish, () => finish(), false);
            }

            if (this.categoriesLoading) {
                this.searchCategoryEntries(
                    {
                        search,
                        limit: 6,
                        outletId: this.outletId,
                        categoryId: this.searchCategory ? this.categoryId : null,
                    },
                    (categories, total) => {
                        if (!isCurrentSearch()) return;
                        this.categoryResults = categories;
                        this.categoryTotal = total;
                        this.categoriesLoading = false;
                    }
                );
            }
        },

        convertProductToSearchResult(products) {
            return Object.values(products)
                .filter((product) => product.status == "publish")
                .map((product) => {
                    var imagePath = "";
                    Object.values(product.images).forEach((details) => {
                        imagePath = details.source_url_small;
                    });

                    return {
                        id: product.id,
                        image: imagePath ? imagePath : "/assets/graphics/img-product.svg",
                        name: product.name,
                        sku: product.sku,
                        barcode: product.barcode,
                        description: product.description,
                    };
                });
        },

        async clickProduct(product) {
            await Promise.all([this.waitForSearchPreferenceSave(), this.recordSearch(this.searchValue)]);
            window.location.href = `${this.homepageUrl}/${this.outletId}/product_details/${product.id}`;
        },

        async clickCategory(category) {
            await Promise.all([this.waitForSearchPreferenceSave(), this.recordSearch(this.searchValue)]);
            window.location.href = category.url;
        },

        // a history chip searches that term straight away
        searchHistoryTerm(term) {
            this.leavingForResults = true;
            this.searchValue = term;
            this.goToSearchResults();
        },

        closeSearchPanel() {
            this.editingSearchHistory = false;
            if (typeof window.closeSearchPanel === "function") {
                window.closeSearchPanel();
            }
        },

        dismissKeyboard() {
            if (document.activeElement === this.$refs.searchInput) {
                this.$refs.searchInput.blur();
            }
        },

        deactivateIfNeeded() {
            this.showSearch = false;
        },

        ignoreLoadingElement(event) {
            if (event.target.classList.contains("loading")) return false;
            return true;
        },

        checkKeyPress(event) {
            if (event.key === "Enter") {
                this.dismissKeyboard();
                this.goToSearchResults();
            }
        },

        // full results page; carries every selected field so it knows whether to show related categories
        async goToSearchResults() {
            if (this.searchValue.trim() == "") return;

            await Promise.all([this.waitForSearchPreferenceSave(), this.recordSearch(this.searchValue)]);

            var encodedSearchString = encodeURIComponent(encodeURIComponent(this.searchValue.trim()));
            var searchFieldString = this.selectedFields.map((field) => "luceneSearchField[]=" + field).join("&");

            window.location.href =
                this.searchResultUrl +
                "?luceneSearchString=" +
                encodedSearchString +
                "&" +
                searchFieldString +
                (this.categoryId ? "&categoryId=" + this.categoryId : "") +
                "&isCategorySearch=" +
                this.searchCategory;
        },

        toggleCategoryFilter() {
            if (!this.isHomePage) {
                this.searchCategory = !this.searchCategory;
                this.searchIfNeeded();
            }
        },
    },
};
</script>
